import moment from 'moment';
import React from 'react';
import SectionTitle from '../shared/SectionTitle';
import SectionMessage from '../shared/SectionMessage';
import SectionCompetitorResults from '../shared/SectionCompetitorResults';
import SectionTooltip from '../shared/SectionTooltip';
import { SectionResult } from '../../../types';
import { useLazyFetchBacklinksQuery } from '../../../../backlinks/service';
import { Icon } from 'antd';
import { SectionProps } from '../types';

function formatDate(dateStr: string) {
    let inputFormat = moment(dateStr, 'YYYY-MM-DDTHH:mm:ss');

    // Old reports have the date as unix timestamp
    if (!inputFormat.isValid()) {
        inputFormat = moment.unix(Number.parseInt(dateStr));
    }

    return inputFormat.format('MMM DD, YYYY');
}

export default function BacklinksSection({ url, sectionKey, title, tooltip, relevance, passRate, result, competitorsResults }: SectionProps) {
    const hasCompetitors = competitorsResults && Object.keys(competitorsResults).length > 0;

    let renderResultComponent: (result: SectionResult) => React.ReactNode = () => null;

    if (Object.hasOwn(result, 'backlinks_list')) {
        renderResultComponent = (result) => <BacklinksResult backlinks={result.backlinks_list} />;
    } else if (Object.hasOwn(result, 'domain')) {
        renderResultComponent = (result) => <BacklinksButton domain={result.domain} />;
    }

    return (
        <div
            id={sectionKey}
            className={`report-element ${result.status} backlinks`}
        >
            <SectionTitle
                title={title}
                status={result.status}
                passRate={passRate}
            />

            <div className="report-element-description">
                <SectionMessage
                    url={url}
                    status={result.status}
                    relevance={relevance}
                    message={result.message}
                    hasCompetitors={hasCompetitors}
                />

                {renderResultComponent(result)}

                {hasCompetitors && (
                    <SectionCompetitorResults
                        results={competitorsResults}
                        relevance={relevance}
                        render={(result) => {
                            let renderResultComponent: (result: SectionResult) => React.ReactNode = () => null;

                            if (Object.hasOwn(result, 'backlinks_list')) {
                                renderResultComponent = (result) => <BacklinksResult backlinks={result.backlinks_list} />;
                            } else if (Object.hasOwn(result, 'domain')) {
                                renderResultComponent = (result) => <BacklinksButton domain={result.domain} />;
                            }

                            return <>{renderResultComponent(result)}</>
                        }}
                    />
                )}
            </div>

            <SectionTooltip tooltip={tooltip} />
        </div>
    );
}

type BacklinksResultProps = {
    backlinks: {
        first_seen: string;
        url_from?: string;
        source_url?: string;
    }[];
};
function BacklinksResult({ backlinks }: BacklinksResultProps) {
    return (
        <>
            {backlinks && backlinks.length > 0 && (
                <div className="description-meta">
                    {backlinks.map((backlinkUrl, index) => (
                        <div
                            key={index}
                            style={{ marginBottom: '10px' }}
                        >
                            <div className="flexbox align-center">
                                <span className="item-label">{`${formatDate(backlinkUrl.first_seen)}`}</span>&nbsp;
                                <a
                                    href={backlinkUrl.url_from || backlinkUrl.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {backlinkUrl.url_from || backlinkUrl.source_url}
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

type BacklinksButtonProps = {
    domain: string;
};

function BacklinksButton({ domain }: BacklinksButtonProps) {
    const [fetchBacklinks, { isLoading: isLoadingBacklinks, isSuccess: isBacklinksFetchSuccess, data: backlinks }] = useLazyFetchBacklinksQuery();

    const handleShowBacklinksClicked = () => {
        fetchBacklinks({ domain: domain, order_by: 'first_seen', order_dir: 'desc', offset: 0 });
    };

    const showBacklinks = isBacklinksFetchSuccess && !!backlinks;

    return (
        <>
            {showBacklinks && <BacklinksResult backlinks={backlinks.data.slice(0, 10)} />}

            {!showBacklinks && (
                <div
                    className="description-meta"
                    style={{ marginTop: '8px' }}
                >
                    <span
                        className={`inline-modal-trigger fake-link full-list ${isLoadingBacklinks ? 'loading' : ''}`}
                        onClick={handleShowBacklinksClicked}
                    >
                        <Icon type={isLoadingBacklinks ? 'loading' : 'caret-right'} /> See results list
                    </span>
                </div>
            )}
        </>
    );
}
